import { BASE_URL } from 'constants/env';
import { parse } from 'utils/error-parser';
import { get } from 'utils/http';

type UploadableImage = {
    name: string;
    type: string;
    blob: Blob;
    width: number;
    height: number;
};

type SignedUploadableImage = {
    image: UploadableImage;
    resourceUrl: string;
    uniqueObjectName: string;
    uploadUrl: string;
};

type SignedUrlResponse = {
    resource_url: string;
    unique_object_name: string;
    upload_url: string;
};

const DEFAULT_HEADERS: Record<string, string> = {
    Accept: 'text/plain',
    'x-amz-acl': 'public-read',
    'Cache-Control': 'max-age=2628000',
};

function uploadToSignedUrl(uploadUrl: string, headers: Record<string, string>, payload: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);

        Object.entries(headers).forEach(([name, value]) => {
            xhr.setRequestHeader(name, value);
        });

        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
                resolve();
            }
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status !== 200) {
                reject(new Error('Error uploading'));
            }
        };

        xhr.addEventListener('error', () => reject(new Error('Error uploading')));
        xhr.send(payload);
    });
}

export function signImageToUpload(image: UploadableImage): Promise<SignedUploadableImage> {
    return get<SignedUrlResponse>(
        `${BASE_URL}/cms/cms_media/signed_url?object_name=${encodeURI(image.name)}&object_type=${image.type}`
    )
        .then((result) => ({
            image,
            resourceUrl: result.resource_url,
            uniqueObjectName: result.unique_object_name,
            uploadUrl: result.upload_url,
        }))
        .catch((error: unknown) => Promise.reject(parse(error)));
}

export async function uploadImage(image: SignedUploadableImage): Promise<SignedUploadableImage> {
    const headers = {
        ...DEFAULT_HEADERS,
        'Content-Disposition': `attachment; filename="${encodeURI(image.uniqueObjectName)}"`,
        'Content-Type': image.image.type,
    };

    await uploadToSignedUrl(image.uploadUrl, headers, image.image.blob);
    return image;
}

/**
 * Uploads a raw File (e.g. a PNG icon) to S3 and returns the public URL.
 * Uses Content-Disposition: inline so the file renders in browsers.
 */
export async function uploadRawFile(file: File): Promise<string> {
    const safeName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, '_');
    const uploadable: UploadableImage = {
        name: safeName,
        type: file.type,
        blob: file,
        width: 0,
        height: 0,
    };
    const signed = await signImageToUpload(uploadable);

    const headers = {
        ...DEFAULT_HEADERS,
        'Content-Disposition': `inline; filename="${encodeURI(signed.uniqueObjectName)}"`,
        'Content-Type': file.type,
    };

    await uploadToSignedUrl(signed.uploadUrl, headers, file);
    return signed.resourceUrl;
}
